import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';

export interface MinimizedWindow {
  id: string;
  header: string;
  color?: string;
  icon?: React.ReactNode;
  restore: () => void;
}

// Interface for tracking active window z-index
export interface WindowZIndex {
  id: string;
  zIndex: number;
}

interface AppState {
  windowWidth: number;
  windowHeight: number;
  isOnMobile: boolean;
  minimizedWindows: MinimizedWindow[];
  activeWindows: WindowZIndex[];
  addMinimizedWindow: (window: MinimizedWindow) => void;
  removeMinimizedWindow: (id: string) => void;
  registerWindow: (id: string) => number;
  unregisterWindow: (id: string) => void;
  bringToFront: (id: string) => number;
  // Add more global state properties here as needed
}

const AppStateContext = createContext<AppState | undefined>(undefined);

interface AppStateProviderProps {
  children: ReactNode;
}

export const AppStateProvider: React.FC<AppStateProviderProps> = ({ children }) => {
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);
  const [windowHeight, setWindowHeight] = useState<number>(window.innerHeight);
  const [minimizedWindows, setMinimizedWindows] = useState<MinimizedWindow[]>([]);
  const [activeWindows, setActiveWindows] = useState<WindowZIndex[]>([]);
  const [baseZIndex] = useState<number>(100); // Base z-index for windows

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setWindowHeight(window.innerHeight);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const addMinimizedWindow = (window: MinimizedWindow) => {
    setMinimizedWindows(prev => [...prev, window]);
  };

  const removeMinimizedWindow = (id: string) => {
    setMinimizedWindows(prev => prev.filter(window => window.id !== id));
  };

  // Register a new window and return its initial z-index
  const registerWindow = (id: string): number => {
    // Find the highest current z-index
    const highestZIndex =
      activeWindows.length > 0 ? Math.max(...activeWindows.map(w => w.zIndex)) : baseZIndex;

    // New window gets highest z-index + 1
    const newZIndex = highestZIndex + 1;

    setActiveWindows(prev => [...prev, { id, zIndex: newZIndex }]);
    return newZIndex;
  };

  // Unregister a window when it's closed
  const unregisterWindow = (id: string): void => {
    setActiveWindows(prev => prev.filter(window => window.id !== id));
  };

  // Bring a window to the front
  const bringToFront = (id: string): number => {
    // Find the highest current z-index
    const highestZIndex =
      activeWindows.length > 0 ? Math.max(...activeWindows.map(w => w.zIndex)) : baseZIndex;

    // New z-index for the window being brought to front
    const newZIndex = highestZIndex + 1;

    setActiveWindows(prev =>
      prev.map(window => (window.id === id ? { ...window, zIndex: newZIndex } : window))
    );

    return newZIndex;
  };

  // global state object
  const appState: AppState = {
    windowWidth,
    windowHeight,
    isOnMobile: windowWidth < 600,
    minimizedWindows,
    activeWindows,
    addMinimizedWindow,
    removeMinimizedWindow,
    registerWindow,
    unregisterWindow,
    bringToFront,
    // Add more global state here in the future as needed
  };

  return <AppStateContext.Provider value={appState}>{children}</AppStateContext.Provider>;
};

// Custom hook to use the AppState context
export const useAppState = (): AppState => {
  const context = useContext(AppStateContext);
  if (!context) {
    throw new Error('useAppState must be used within an AppStateProvider');
  }
  return context;
};
